import { c as _c } from "react/compiler-runtime";
import { Fragment, useCallback, useState } from 'react';
import type { Category } from '@sbt-web/category-utils';
import { useViewport } from '@sbt-web/hooks';
import { FilterURI, type ListFilter } from '@sbt-web/network/types';
import { Button, FormGroup, FullModal, SelectBoxSkeleton, SelectOption, SelectBox } from '@sbt-web/ui';
import { useDataSource } from '@/client/hooks/useDataSource';
import { mapValuesToOptions, useValueList, ValueListStatus, type ValueListOption } from '@/client/hooks/useValueList';
import { FilterLabel } from '../common/FilterLabel';
import type { CommonFilterProps } from '../common/types';
import { InfoBadge } from '../InfoBadge';
import commonClasses from '../common/styles.module.scss';
import ownClasses from './list-selector.module.scss';
type LegacySelectBoxType = 'default' | 'radio' | 'multi';
type Props = CommonFilterProps & {
  filter: ListFilter;
  category?: Category;
  dependenciesValues?: {
    [key: string]: string;
  };
  initialValue?: string;
  onSelect: (key: string, value?: string, label?: string) => void;
};
const minOptionsBeforeCollapse = 5;
const collapsedURIs: string[] = [FilterURI.AdvertiserType, FilterURI.MarketClothingGender];
const CLOTHING_TYPE_INFO = 'Seleziona la Tipologia per attivare i filtri Marca e Taglia';
interface HelpTextData {
  title: string;
  content: {
    name: string;
    description?: string;
  }[];
}
const buildHelpTextContent = ({
  content
}: HelpTextData) => content.map(({
  name,
  description
}) => <Fragment key={name}>
      <p className={`body-text semibold ${ownClasses['value-name']}`}>{name}</p>
      <p className={`body-text book ${ownClasses['value-description']}`}>
        {description}
      </p>
    </Fragment>);
const buildInitialIndices = (initialValue: string | undefined, valueList: SelectOption[], filter: ListFilter): number[] => {
  const initialIndices: number[] = [];
  if (initialValue) {
    if (filter.multiSelection) {
      const initialValues = initialValue.split(',');

      // Get the indices of the list based on the values already in the
      // query string to prepopulate the UI.
      for (let index = 0; index < valueList.length; index++) {
        const listItem = valueList[index];
        if (listItem.value && initialValues.includes(listItem.value) && valueList.find(v => v.value === listItem.value)) {
          initialIndices.push(index);
        }
      }
    } else {
      const index = valueList.findIndex(v => v.value === initialValue);
      if (index !== -1) {
        initialIndices.push(index);
      }
    }
  }
  return initialIndices;
};
function buildHelpText(valueList: ValueListOption[], filter: ListFilter): HelpTextData | undefined {
  if (valueList.some(value => value.description)) {
    return {
      title: filter.label,
      content: valueList.map(({
        name,
        description
      }) => ({
        name,
        description
      }))
    };
  }
}
const ListSelector = t0 => {
  const $ = _c(40);
  const {
    filter,
    category,
    dependenciesValues,
    initialValue,
    showBadgeNew,
    onSelect
  } = t0;
  const {
    isMobile
  } = useViewport();
  const flavor = isMobile ? "large" : "small";
  let t1;
  if ($[0] !== filter.queryString || $[1] !== initialValue || $[2] !== onSelect) {
    t1 = options => {
      let newValue;
      let newLabel;
      if (options.length) {
        newValue = options.map(_temp).join(",");
        newLabel = options.map(_temp2).join(", ");
      }
      onSelect(filter.queryString, newValue !== initialValue ? newValue : undefined, newValue !== initialValue ? newLabel : undefined);
    };
    $[0] = filter.queryString;
    $[1] = initialValue;
    $[2] = onSelect;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const handleSelection = t1;
  let t2;
  if ($[4] !== filter.multiSelection || $[5] !== filter.uri) {
    t2 = valueList => {
      if (valueList.length === 1 || filter.multiSelection) {
        return "multi";
      } else {
        if (valueList.length < minOptionsBeforeCollapse && !collapsedURIs.includes(filter.uri)) {
          return "radio";
        } else {
          return "default";
        }
      }
    };
    $[4] = filter.multiSelection;
    $[5] = filter.uri;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const getType = t2;
  let t3;
  if ($[7] !== filter.uri) {
    t3 = valueList_0 => {
      if (collapsedURIs.includes(filter.uri)) {
        return false;
      } else {
        return valueList_0.length < minOptionsBeforeCollapse;
      }
    };
    $[7] = filter.uri;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const getExpanded = t3;
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setIsHelpModalOpen(true);
    };
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const openModal = t4;
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setIsHelpModalOpen(false);
    };
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const closeModal = t5;
  const showClothingTypeInfo = filter.uri === FilterURI.MarketClothingType;
  const {
    dataSource,
    dependenciesFilled
  } = useDataSource(filter.dataSource, filter.dependencies, dependenciesValues);
  const valuelistData = useValueList(dataSource, category?.id, !dependenciesFilled);
  if (valuelistData.state === ValueListStatus.ERROR) {
    return;
  }
  let T0;
  let t6;
  let t7;
  if ($[11] !== filter || $[12] !== flavor || $[13] !== getExpanded || $[14] !== getType || $[15] !== handleSelection || $[16] !== initialValue || $[17] !== isHelpModalOpen || $[18] !== showBadgeNew || $[19] !== valuelistData.state || $[20] !== valuelistData.value) {
    const valueList_1 = mapValuesToOptions(valuelistData.value);
    const initialIndices = buildInitialIndices(initialValue, valueList_1, filter);
    const helpText = buildHelpText(valueList_1, filter);
    const hasHelpText = !!helpText;
    T0 = FormGroup;
    const t8 = hasHelpText && <><InfoBadge label={filter.label} className={`${filter.queryString}-help-button`} onClick={openModal} /><FullModal open={isHelpModalOpen} title={helpText.title} onClose={closeModal} actions={[<Button key="closer" design="solid" onClick={closeModal}>Ok, ho capito</Button>]}>{buildHelpTextContent(helpText)}</FullModal></>;
    let t9;
    if ($[24] !== filter) {
      t9 = <ClothingTypeInfo filter={filter} />;
      $[24] = filter;
      $[25] = t9;
    } else {
      t9 = $[25];
    }
    let t10;
    if ($[26] !== t8 || $[27] !== t9) {
      t10 = <>{t8}{t9}</>;
      $[26] = t8;
      $[27] = t9;
      $[28] = t10;
    } else {
      t10 = $[28];
    }
    if ($[29] !== filter.label || $[30] !== showBadgeNew || $[31] !== t10) {
      t6 = <FilterLabel showBadgeNew={showBadgeNew} extraContent={t10}>{filter.label}</FilterLabel>;
      $[29] = filter.label;
      $[30] = showBadgeNew;
      $[31] = t10;
      $[32] = t6;
    } else {
      t6 = $[32];
    }
    t7 = valuelistData.state === ValueListStatus.LOADED ? <SelectBox name={filter.uri} type={getType(valueList_1)} expanded={getExpanded(valueList_1)} placeholder="Qualsiasi" selectedIndices={initialIndices} nullable={true} flavor={flavor} valueList={valueList_1} onSelect={handleSelection} /> : <SelectBoxSkeleton expanded={filter.multiSelection} flavor={flavor} />;
    $[11] = filter;
    $[12] = flavor;
    $[13] = getExpanded;
    $[14] = getType;
    $[15] = handleSelection;
    $[16] = initialValue;
    $[17] = isHelpModalOpen;
    $[18] = showBadgeNew;
    $[19] = valuelistData.state;
    $[20] = valuelistData.value;
    $[21] = T0;
    $[22] = t6;
    $[23] = t7;
  } else {
    T0 = $[21];
    t6 = $[22];
    t7 = $[23];
  }
  let t8;
  if ($[33] !== showClothingTypeInfo) {
    t8 = showClothingTypeInfo && <p className={`body-text small ${commonClasses["mobile-help-txt"]} ${ownClasses["clothing-info-bottom-text"]}`}>{CLOTHING_TYPE_INFO}</p>;
    $[33] = showClothingTypeInfo;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  let t9;
  if ($[35] !== T0 || $[36] !== t6 || $[37] !== t7 || $[38] !== t8) {
    t9 = <T0>{t6}{t7}{t8}</T0>;
    $[35] = T0;
    $[36] = t6;
    $[37] = t7;
    $[38] = t8;
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  return t9;
};
const ClothingTypeInfo = t0 => {
  const $ = _c(11);
  const {
    filter
  } = t0;
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setInfoModalOpen(false);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const closeModal = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setInfoModalOpen(true);
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const openModal = t2;
  if (filter.uri !== FilterURI.MarketClothingType) {
    return;
  }
  let t3;
  if ($[2] !== filter.label) {
    t3 = <InfoBadge label={filter.label} onClick={openModal} className={ownClasses["clothing-info-badge"]} />;
    $[2] = filter.label;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [<Button key="closer" design="solid" onClick={closeModal}>Ok, ho capito</Button>];
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== filter.label || $[6] !== infoModalOpen) {
    t5 = <FullModal open={infoModalOpen} title={filter.label} onClose={closeModal} actions={t4}>{CLOTHING_TYPE_INFO}</FullModal>;
    $[5] = filter.label;
    $[6] = infoModalOpen;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t3 || $[9] !== t5) {
    t6 = <>{t3}{t5}</>;
    $[8] = t3;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
};
export default ListSelector;
function _temp(option) {
  return option.value;
}
function _temp2(option_0) {
  return option_0.name;
}